import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { CircuitBoard, Minus, Plus, Save, Trash2, Upload, Wrench } from 'lucide-react';
import { adminPassiveComponentsAPI } from '../services/adminApi';
import { prepareInlineSvgMarkup } from '../utils/inlineSvg';
import {
  PASSIVE_SIMULATION_BEHAVIOR_OPTIONS,
  PASSIVE_SIMULATION_INTERACTION_OPTIONS,
  PASSIVE_SIMULATION_RULE_CONDITION_OPTIONS,
  PASSIVE_TEMPLATE_OPTIONS,
  VARIABLE_RESISTOR_STIMULUS_OUTPUT_OPTIONS,
  buildDefaultPassiveSimulationModel,
  buildDefaultPassivePinOffsets,
  getDefaultPassiveValue,
  getPassiveTemplateDefaults,
  getPassiveTemplateLabel,
  getPassiveTemplatePinKeys,
  normalizePassiveComponentDefinition,
  normalizePassivePinOffsets,
  normalizePassiveSimulationModel,
  resolveVariableResistorSimulationState,
  validateVariableResistorStimulusFormula,
} from '../utils/projectBuilderPassives';
import { buildPassivePreviewSvgData } from '../utils/projectBuilderPassiveSvg';

const CATALOG_THUMB_WIDTH = 84;
const CATALOG_THUMB_HEIGHT = 64;
const PREVIEW_STAGE_WIDTH = 360;
const PREVIEW_STAGE_HEIGHT = 220;
const PREVIEW_STAGE_PADDING = 24;
const PREVIEW_ZOOM_DEFAULT = 1;
const PREVIEW_ZOOM_MIN = 0.2;
const PREVIEW_ZOOM_MAX = 2.5;
const PREVIEW_ZOOM_STEP = 0.25;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Panel = styled.section`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  padding: 18px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const Title = styled.h3`
  margin: 0;
  color: #111827;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Text = styled.p`
  margin: 6px 0 0 0;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  border-radius: 10px;
  padding: 9px 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #93c5fd;
    background: #f8fbff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(SecondaryButton)`
  border-color: #2c9fa4;
  background: #2c9fa4;
  color: #ffffff;

  &:hover:not(:disabled) {
    border-color: #228388;
    background: #228388;
  }
`;

const PassiveList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 12px;
  max-height: 520px;
  overflow-y: auto;
  padding-right: 4px;
`;

const PassiveCard = styled.button`
  width: 100%;
  border: 1px solid ${props => props.$active ? '#7dd3fc' : '#e5e7eb'};
  background: ${props => props.$active ? '#f0f9ff' : '#ffffff'};
  border-radius: 12px;
  padding: 12px;
  text-align: left;
  cursor: pointer;
  display: grid;
  grid-template-columns: ${props => `${props.$thumbWidth || 84}px minmax(0, 1fr)`};
  gap: 12px;
  align-items: start;

  &:hover {
    border-color: #7dd3fc;
    background: #f8fbff;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PassiveThumb = styled.div`
  width: ${props => `${props.$width || 84}px`};
  height: ${props => `${props.$height || 64}px`};
  border-radius: 10px;
  border: 1px solid #dbeafe;
  background: linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  svg {
    width: 100% !important;
    height: 100% !important;
    display: block;
  }
`;

const PassiveThumbSvg = styled.div`
  width: 100%;
  height: 100%;

  > svg {
    width: 100%;
    height: 100%;
  }
`;

const PassiveInfo = styled.div`
  min-width: 0;
`;

const PassiveName = styled.div`
  color: #111827;
  font-size: 0.94rem;
  font-weight: 800;
`;

const PassiveMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 999px;
  background: ${props => props.$tone === 'warning' ? '#fff7ed' : props.$tone === 'muted' ? '#f1f5f9' : '#eff6ff'};
  color: ${props => props.$tone === 'warning' ? '#c2410c' : props.$tone === 'muted' ? '#475569' : '#1d4ed8'};
  font-size: 0.72rem;
  font-weight: 800;
`;

const PassiveDescription = styled.p`
  margin: 8px 0 0 0;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  border: 1px dashed #cbd5e1;
  border-radius: 12px;
  padding: 18px;
  color: #64748b;
  font-size: 0.9rem;
  line-height: 1.55;
  background: #f8fafc;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 700;
`;

const HelperText = styled.div`
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.5;
`;

const Input = styled.input`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }

  &:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 88px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }
`;

const Select = styled.select`
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;
  background: #ffffff;

  &:focus {
    outline: none;
    border-color: #2c9fa4;
    box-shadow: 0 0 0 3px rgba(44, 159, 164, 0.14);
  }

  &:disabled {
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
  }
`;

const ToggleLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: pointer;
`;

const Toggle = styled.input`
  width: 16px;
  height: 16px;
`;

const UploadBox = styled.div`
  border: 1px dashed #93c5fd;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const UploadMeta = styled.div`
  color: #475569;
  font-size: 0.82rem;
`;

const PreviewToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const PreviewToolbarText = styled.div`
  color: #0f172a;
  font-size: 0.84rem;
  font-weight: 700;
`;

const ZoomControls = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ZoomButton = styled.button`
  width: 34px;
  height: 34px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  background: #ffffff;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #93c5fd;
    background: #f8fbff;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const ZoomResetButton = styled(SecondaryButton)`
  padding: 7px 10px;
  font-size: 0.8rem;
`;

const ZoomReadout = styled.div`
  min-width: 56px;
  text-align: center;
  color: #475569;
  font-size: 0.8rem;
  font-weight: 800;
`;

const PreviewFrame = styled.div`
  width: 100%;
  min-height: 240px;
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fbff 0%, #eef6ff 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
  padding: 18px;

  svg {
    max-width: none;
    max-height: none;
  }
`;

const PreviewSurface = styled.div`
  width: ${props => `${props.$width || 360}px`};
  height: ${props => `${props.$height || 220}px`};
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewCanvasBoundary = styled.div`
  position: relative;
  width: ${props => `${props.$width || 120}px`};
  height: ${props => `${props.$height || 80}px`};
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  background: rgba(255, 255, 255, 0.88);
  box-shadow:
    0 10px 24px rgba(14, 116, 144, 0.12),
    inset 0 0 0 1px rgba(14, 165, 233, 0.12);

  &::before,
  &::after {
    content: '';
    position: absolute;
    pointer-events: none;
    z-index: 2;
  }

  &::before {
    inset: 0;
    border: 2px dashed rgba(2, 132, 199, 0.95);
    border-radius: 12px;
  }

  &::after {
    inset: 0;
    background:
      linear-gradient(
        90deg,
        transparent calc(50% - 0.5px),
        rgba(14, 116, 144, 0.22) calc(50% - 0.5px),
        rgba(14, 116, 144, 0.22) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      ),
      linear-gradient(
        180deg,
        transparent calc(50% - 0.5px),
        rgba(14, 116, 144, 0.22) calc(50% - 0.5px),
        rgba(14, 116, 144, 0.22) calc(50% + 0.5px),
        transparent calc(50% + 0.5px)
      );
    border-radius: 12px;
  }
`;

const PreviewCanvasBadge = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 3;
  padding: 5px 8px;
  border-radius: 999px;
  background: rgba(8, 47, 73, 0.92);
  color: #e0f2fe;
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  white-space: nowrap;
  pointer-events: none;
`;

const PreviewSvg = styled.div`
  width: 100%;
  height: 100%;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;
  position: relative;
  z-index: 1;

  > svg {
    width: 100% !important;
    height: 100% !important;
    max-width: none !important;
    max-height: none !important;
    flex: 0 0 auto;
    display: block !important;
    overflow: visible !important;
  }
`;

const PreviewEmptyState = styled.div`
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: #64748b;
  font-size: 0.8rem;
  line-height: 1.5;
  position: relative;
  z-index: 1;
`;

const PreviewMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const PinOffsetPanel = styled.div`
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: #f8fbff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PinOffsetHeader = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;

  @media (max-width: 640px) {
    display: none;
  }
`;

const PinOffsetRow = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr) minmax(0, 1fr);
  gap: 10px;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const PinOffsetKey = styled.div`
  color: #0f172a;
  font-size: 0.82rem;
  font-weight: 800;
`;

const ValueRowList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const ValueRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SimulationPanel = styled.div`
  border: 1px solid #dbeafe;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fbff 0%, #f1f8ff 100%);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SimulationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const SimulationTitle = styled.div`
  color: #0f172a;
  font-size: 0.9rem;
  font-weight: 800;
`;

const SimulationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SimulationSubTitle = styled.div`
  color: #334155;
  font-size: 0.8rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const RoleGridHeader = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 10px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;

  @media (max-width: 640px) {
    display: none;
  }
`;

const RoleGridRow = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(0, 1fr);
  gap: 10px;
  align-items: center;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RuleHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  color: #64748b;
  font-size: 0.76rem;
  font-weight: 800;

  @media (max-width: 720px) {
    display: none;
  }
`;

const RuleRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const JsonPreview = styled.pre`
  margin: 0;
  padding: 14px;
  border-radius: 12px;
  background: #0f172a;
  color: #dbeafe;
  font-size: 0.78rem;
  line-height: 1.55;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const InlineDangerButton = styled.button`
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #be123c;
  border-radius: 10px;
  padding: 10px 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.84rem;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: #fda4af;
    background: #ffe4e6;
  }
`;

const FormFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const StatusText = styled.div`
  color: ${props => props.$tone === 'error' ? '#b91c1c' : props.$tone === 'success' ? '#166534' : '#64748b'};
  font-size: 0.84rem;
  line-height: 1.5;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const slugifyKey = (value) => (
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

const clampPreviewZoom = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return PREVIEW_ZOOM_DEFAULT;
  }

  return Math.min(PREVIEW_ZOOM_MAX, Math.max(PREVIEW_ZOOM_MIN, Math.round(numericValue * 100) / 100));
};

const formatPreviewDimension = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return '0';
  }

  const roundedValue = Math.round(numericValue * 10) / 10;
  return Number.isInteger(roundedValue) ? String(roundedValue) : String(roundedValue);
};

const formatSimulationParametersText = (parameters) => (
  JSON.stringify(
    (
      parameters &&
      typeof parameters === 'object' &&
      !Array.isArray(parameters)
    ) ? parameters : {},
    null,
    2
  )
);

const parseSimulationParametersText = (value) => {
  const trimmedValue = String(value ?? '').trim();

  if (
    !trimmedValue ||
    trimmedValue === 'null' ||
    trimmedValue === 'undefined' ||
    trimmedValue === '[]'
  ) {
    return {};
  }

  let parsedValue;

  try {
    parsedValue = JSON.parse(trimmedValue);
  } catch {
    throw new Error('Simulation parameters must be valid JSON.');
  }

  if (parsedValue === null) {
    return {};
  }

  if (Array.isArray(parsedValue) && parsedValue.length === 0) {
    return {};
  }

  if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
    throw new Error('Simulation parameters must be a JSON object.');
  }

  return parsedValue;
};

const tryParseSimulationParametersText = (value, fallbackValue = {}) => {
  try {
    return parseSimulationParametersText(value);
  } catch {
    if (
      fallbackValue &&
      typeof fallbackValue === 'object' &&
      !Array.isArray(fallbackValue)
    ) {
      return JSON.parse(JSON.stringify(fallbackValue));
    }

    return {};
  }
};

const createSimulationFormState = (templateType, simulationModel) => {
  const normalizedModel = normalizePassiveSimulationModel(
    simulationModel ?? buildDefaultPassiveSimulationModel(templateType),
    templateType
  );

  return {
    simulationModel: normalizedModel,
    simulationParametersText: formatSimulationParametersText(normalizedModel.parameters),
  };
};

const buildSimulationModelFromForm = (form) => normalizePassiveSimulationModel(
  {
    ...(form?.simulationModel || {}),
    parameters: parseSimulationParametersText(form?.simulationParametersText),
  },
  form?.templateType
);

const createEmptyForm = () => {
  const defaults = getPassiveTemplateDefaults('generic_2pin');
  const simulationState = createSimulationFormState('generic_2pin');

  return {
    id: '',
    componentKey: '',
    name: '',
    description: defaults.description,
    templateType: 'generic_2pin',
    svgContent: '',
    valueOptions: [],
    defaultValue: '',
    sortOrder: 0,
    isActive: true,
    isSystem: false,
    showNoSimulationTag: false,
    pinOffsets: buildDefaultPassivePinOffsets('generic_2pin'),
    canvasWidth: defaults.canvasWidth,
    canvasHeight: defaults.canvasHeight,
    svgFilename: '',
    ...simulationState,
  };
};

const renderPreviewSvg = (svgContent) => {
  const markup = String(svgContent || '').trim();

  if (!markup) {
    return '';
  }

  return prepareInlineSvgMarkup(markup);
};

const getPassivePreviewData = (item) => (
  buildPassivePreviewSvgData({
    passiveType: item?.templateType,
    svgContent: item?.svgContent,
    pinOffsets: item?.pinOffsets,
    sevenSegmentDigitCount: item?.templateType === 'seven_segment_display' ? 4 : undefined,
    sevenSegmentIsPowered: item?.templateType === 'seven_segment_display',
    valueOhms: Number(getDefaultPassiveValue(item) || 10000),
  })
);

const ProjectBuilderPassiveManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(createEmptyForm);
  const [selectedId, setSelectedId] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editorPreviewZoom, setEditorPreviewZoom] = useState(PREVIEW_ZOOM_DEFAULT);
  const fileInputRef = useRef(null);

  const updateEditorPreviewZoom = useCallback((nextValue) => {
    setEditorPreviewZoom(clampPreviewZoom(nextValue));
  }, []);

  const sortedItems = useMemo(
    () => [...items].sort((left, right) => {
      if (left.sortOrder !== right.sortOrder) {
        return left.sortOrder - right.sortOrder;
      }

      return String(left.name || '').localeCompare(String(right.name || ''));
    }),
    [items]
  );

  const selectedItem = useMemo(
    () => sortedItems.find((item) => String(item.id) === String(selectedId)) || null,
    [selectedId, sortedItems]
  );

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminPassiveComponentsAPI.getAll();
      const nextItems = Array.isArray(response?.data)
        ? response.data.map(normalizePassiveComponentDefinition).filter(Boolean)
        : [];

      setItems(nextItems);
      setSelectedId((currentId) => currentId || String(nextItems[0]?.id || ''));
    } catch (loadError) {
      setError(loadError?.response?.data?.error || 'Failed to load basic parts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const startCreate = () => {
    setIsEditing(false);
    setSelectedId('');
    setSuccess('');
    setError('');
    setForm(createEmptyForm());
  };

  const startEdit = (item) => {
    if (!item) {
      return;
    }

    const simulationState = createSimulationFormState(item.templateType, item.simulationModel);

    setIsEditing(true);
    setSelectedId(String(item.id));
    setSuccess('');
    setError('');
    setForm({
      id: item.id,
      componentKey: item.componentKey,
      name: item.name,
      description: item.description || '',
      templateType: item.templateType,
      svgContent: item.svgContent || '',
      valueOptions: Array.isArray(item.valueOptions) ? item.valueOptions : [],
      defaultValue: item.defaultValue ?? '',
      sortOrder: item.sortOrder ?? 0,
      isActive: Boolean(item.isActive),
      isSystem: Boolean(item.isSystem),
      showNoSimulationTag: Boolean(item.showNoSimulationTag),
      pinOffsets: normalizePassivePinOffsets(item.pinOffsets, item.templateType),
      canvasWidth: item.canvasWidth,
      canvasHeight: item.canvasHeight,
      svgFilename: '',
      ...simulationState,
    });
  };

  const handleFieldChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleNameChange = (value) => {
    setForm((current) => {
      const next = {
        ...current,
        name: value,
      };

      if (!isEditing && !current.componentKey) {
        next.componentKey = slugifyKey(value);
      }

      return next;
    });
  };

  const handleTemplateChange = (value) => {
    const defaults = getPassiveTemplateDefaults(value);
    const simulationState = createSimulationFormState(value);

    setForm((current) => ({
      ...current,
      templateType: value,
      description:
        !current.description || current.description === getPassiveTemplateDefaults(current.templateType).description
          ? defaults.description
          : current.description,
      canvasWidth: defaults.canvasWidth,
      canvasHeight: defaults.canvasHeight,
      pinOffsets: buildDefaultPassivePinOffsets(value),
      valueOptions: defaults.valueOptions,
      defaultValue: getDefaultPassiveValue({
        templateType: value,
        valueOptions: defaults.valueOptions,
        defaultValue: defaults.defaultValue,
      }),
      ...simulationState,
    }));
  };

  const handleBrowseSvg = () => {
    fileInputRef.current?.click();
  };

  const handleSvgUpload = async (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    try {
      const svgText = await file.text();

      setForm((current) => ({
        ...current,
        svgContent: svgText,
        svgFilename: file.name,
      }));

      setError('');
    } catch {
      setError('Failed to read the selected SVG file.');
    } finally {
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const handleAddValueOption = () => {
    setForm((current) => ({
      ...current,
      valueOptions: [
        ...(Array.isArray(current.valueOptions) ? current.valueOptions : []),
        { label: '', value: '' },
      ],
    }));
  };

  const handleValueOptionChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      valueOptions: (current.valueOptions || []).map((option, optionIndex) =>
        optionIndex === index
          ? {
              ...option,
              [field]: value,
            }
          : option
      ),
    }));
  };

  const handleRemoveValueOption = (index) => {
    setForm((current) => {
      const nextOptions = (current.valueOptions || []).filter((_, optionIndex) => optionIndex !== index);
      const nextDefaultExists = nextOptions.some((option) => String(option.value) === String(current.defaultValue));

      return {
        ...current,
        valueOptions: nextOptions,
        defaultValue: nextDefaultExists ? current.defaultValue : (nextOptions[0]?.value ?? ''),
      };
    });
  };

  const handlePinOffsetChange = (pinKey, axis, value) => {
    setForm((current) => ({
      ...current,
      pinOffsets: {
        ...(current.pinOffsets || {}),
        [pinKey]: {
          x: current.pinOffsets?.[pinKey]?.x ?? 0,
          y: current.pinOffsets?.[pinKey]?.y ?? 0,
          [axis]: value,
        },
      },
    }));
  };

  const handleSimulationFieldChange = (field, value) => {
    setForm((current) => ({
      ...current,
      simulationModel: {
        ...(current.simulationModel || {}),
        [field]: value,
      },
    }));
  };

  const handleSimulationParametersChange = (updater) => {
    setForm((current) => {
      const nextParameters = updater(
        tryParseSimulationParametersText(
          current.simulationParametersText,
          current.simulationModel?.parameters || {}
        )
      );

      return {
        ...current,
        simulationParametersText: formatSimulationParametersText(nextParameters),
        simulationModel: {
          ...(current.simulationModel || {}),
          parameters: nextParameters,
        },
      };
    });
  };

  const handleSimulationParameterFieldChange = (field, value, { removeIfBlank = false } = {}) => {
    handleSimulationParametersChange((currentParameters) => {
      const nextParameters = {
        ...(currentParameters || {}),
      };

      if (removeIfBlank && (value === '' || value === null || value === undefined)) {
        delete nextParameters[field];
        return nextParameters;
      }

      nextParameters[field] = value;
      return nextParameters;
    });
  };

  const handleSimulationTerminalRoleChange = (pinKey, value) => {
    setForm((current) => ({
      ...current,
      simulationModel: {
        ...(current.simulationModel || {}),
        terminalRoles: {
          ...(current.simulationModel?.terminalRoles || {}),
          [pinKey]: value,
        },
      },
    }));
  };

  const handleAddConductionRule = () => {
    const [firstPinKey = '1', secondPinKey = '2'] = getPassiveTemplatePinKeys(form.templateType);

    setForm((current) => ({
      ...current,
      simulationModel: {
        ...(current.simulationModel || {}),
        conductionRules: [
          ...(Array.isArray(current.simulationModel?.conductionRules)
            ? current.simulationModel.conductionRules
            : []),
          {
            fromPinKey: firstPinKey,
            toPinKey: secondPinKey || firstPinKey,
            when: 'always',
          },
        ],
      },
    }));
  };

  const handleConductionRuleChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      simulationModel: {
        ...(current.simulationModel || {}),
        conductionRules: (Array.isArray(current.simulationModel?.conductionRules)
          ? current.simulationModel.conductionRules
          : []
        ).map((rule, ruleIndex) => (
          ruleIndex === index
            ? {
              ...rule,
              [field]: value,
            }
            : rule
        )),
      },
    }));
  };

  const handleRemoveConductionRule = (index) => {
    setForm((current) => ({
      ...current,
      simulationModel: {
        ...(current.simulationModel || {}),
        conductionRules: (Array.isArray(current.simulationModel?.conductionRules)
          ? current.simulationModel.conductionRules
          : []
        ).filter((_, ruleIndex) => ruleIndex !== index),
      },
    }));
  };

  const handleResetSimulationModel = () => {
    const simulationState = createSimulationFormState(form.templateType);

    setForm((current) => ({
      ...current,
      ...simulationState,
    }));
  };

  const handleDelete = async (item) => {
    if (!item || item.isSystem) {
      return;
    }

    const shouldDelete = window.confirm(`Delete "${item.name}" from the basic parts catalog?`);

    if (!shouldDelete) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      await adminPassiveComponentsAPI.delete(item.id);
      await loadItems();

      if (String(selectedId) === String(item.id)) {
        startCreate();
      }

      setSuccess(`Deleted "${item.name}".`);
    } catch (deleteError) {
      setError(deleteError?.response?.data?.error || 'Failed to delete basic part.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (simulationParametersError) {
      setError(simulationParametersError);
      return;
    }

    if (sensorFormulaError) {
      setError(sensorFormulaError);
      return;
    }

    try {
      setSaving(true);
      setError('');
      setSuccess('');
      const simulationModel = buildSimulationModelFromForm(form);

      const payload = {
        name: form.name,
        description: form.description,
        svgContent: form.svgContent,
        valueOptions: form.valueOptions,
        pinOffsets: normalizePassivePinOffsets(form.pinOffsets, form.templateType),
        simulationModel,
        showNoSimulationTag: form.showNoSimulationTag,
        defaultValue: form.valueOptions.length > 0 ? form.defaultValue : null,
        sortOrder: Number(form.sortOrder || 0),
        isActive: form.isActive,
        canvasWidth: Number(form.canvasWidth || 0),
        canvasHeight: Number(form.canvasHeight || 0),
      };
      if (!isEditing) {
        payload.componentKey = form.componentKey || slugifyKey(form.name);
        payload.templateType = form.templateType;
      }

      const response = isEditing
        ? await adminPassiveComponentsAPI.update(form.id, payload)
        : await adminPassiveComponentsAPI.create(payload);

      const savedItem = normalizePassiveComponentDefinition(response?.data?.data || response?.data);
      await loadItems();

      if (savedItem?.id) {
        setSelectedId(String(savedItem.id));
        startEdit(savedItem);
      }

      setSuccess(isEditing ? 'Basic part updated.' : 'Basic part created.');
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Failed to save basic part.');
    } finally {
      setSaving(false);
    }
  };

  const templatePinKeys = useMemo(
    () => getPassiveTemplatePinKeys(form.templateType),
    [form.templateType]
  );
  const normalizedPinOffsets = useMemo(
    () => normalizePassivePinOffsets(form.pinOffsets, form.templateType),
    [form.pinOffsets, form.templateType]
  );
  const simulationParametersError = useMemo(() => {
    try {
      parseSimulationParametersText(form.simulationParametersText);
      return '';
    } catch (validationError) {
      return validationError.message || 'Simulation parameters must be valid JSON.';
    }
  }, [form.simulationParametersText]);
  const normalizedSimulationModel = useMemo(() => {
    try {
      return buildSimulationModelFromForm(form);
    } catch {
      return normalizePassiveSimulationModel(
        {
          ...(form.simulationModel || {}),
          parameters: form.simulationModel?.parameters || {},
        },
        form.templateType
      );
    }
  }, [form]);
  const simulationParametersDraft = useMemo(
    () => tryParseSimulationParametersText(
      form.simulationParametersText,
      form.simulationModel?.parameters || {}
    ),
    [form.simulationModel?.parameters, form.simulationParametersText]
  );
  const supportsVariableResistorSensorControls = useMemo(() => {
    const modelKey = String(normalizedSimulationModel?.modelKey || '').trim();
    const behavior = String(normalizedSimulationModel?.behavior || '').trim();

    return (
      behavior === 'variable_resistor' ||
      modelKey === 'variable_resistor' ||
      modelKey === 'potentiometer' ||
      form.templateType === 'potentiometer'
    );
  }, [form.templateType, normalizedSimulationModel?.behavior, normalizedSimulationModel?.modelKey]);
  const variableResistorSensorState = useMemo(
    () => resolveVariableResistorSimulationState(normalizedSimulationModel?.parameters || {}),
    [normalizedSimulationModel?.parameters]
  );
  const sensorFormulaError = useMemo(() => {
    if (
      simulationParametersError ||
      !supportsVariableResistorSensorControls ||
      !variableResistorSensorState.hasStimulusControl
    ) {
      return '';
    }

    return validateVariableResistorStimulusFormula(
      simulationParametersDraft?.stimulusFormula
    );
  }, [
    simulationParametersDraft?.stimulusFormula,
    simulationParametersError,
    supportsVariableResistorSensorControls,
    variableResistorSensorState.hasStimulusControl,
  ]);
  const previewCanvasSize = useMemo(() => {
    const defaults = getPassiveTemplateDefaults(form.templateType);
    const width = Number(form.canvasWidth);
    const height = Number(form.canvasHeight);

    return {
      width: Number.isFinite(width) && width > 0 ? width : Number(defaults.canvasWidth) || 1,
      height: Number.isFinite(height) && height > 0 ? height : Number(defaults.canvasHeight) || 1,
    };
  }, [form.canvasHeight, form.canvasWidth, form.templateType]);
  const previewCanvasDisplay = useMemo(() => {
    const availableWidth = Math.max(1, PREVIEW_STAGE_WIDTH - (PREVIEW_STAGE_PADDING * 2));
    const availableHeight = Math.max(1, PREVIEW_STAGE_HEIGHT - (PREVIEW_STAGE_PADDING * 2));
    const fitScale = Math.min(
      availableWidth / previewCanvasSize.width,
      availableHeight / previewCanvasSize.height
    );
    const resolvedFitScale = Number.isFinite(fitScale) && fitScale > 0 ? fitScale : 1;
    const resolvedScale = resolvedFitScale * editorPreviewZoom;
    const width = Math.max(1, previewCanvasSize.width * resolvedScale);
    const height = Math.max(1, previewCanvasSize.height * resolvedScale);

    return {
      width,
      height,
      surfaceWidth: Math.max(PREVIEW_STAGE_WIDTH, Math.ceil(width + (PREVIEW_STAGE_PADDING * 2))),
      surfaceHeight: Math.max(PREVIEW_STAGE_HEIGHT, Math.ceil(height + (PREVIEW_STAGE_PADDING * 2))),
    };
  }, [editorPreviewZoom, previewCanvasSize]);
  const previewData = useMemo(
    () => getPassivePreviewData({
      templateType: form.templateType,
      svgContent: form.svgContent,
      pinOffsets: normalizedPinOffsets,
      defaultValue: form.defaultValue,
      valueOptions: form.valueOptions,
    }),
    [form.defaultValue, form.svgContent, form.templateType, form.valueOptions, normalizedPinOffsets]
  );
  const detectedPinKeys = useMemo(
    () => previewData.pins.map((pin) => pin.pinKey),
    [previewData.pins]
  );
  const missingPinKeys = useMemo(
    () => templatePinKeys.filter((pinKey) => !detectedPinKeys.includes(pinKey)),
    [detectedPinKeys, templatePinKeys]
  );

  return (
    <Wrapper>
      <Panel>
        <Header>
          <div>
            <Title>
              <CircuitBoard size={18} />
              Basic Parts Catalog
            </Title>
            <Text>
              Add or update the basic parts shown in Builder. The LED stays on its built-in special path.
            </Text>
          </div>

          <HeaderActions>
            <SecondaryButton type="button" onClick={startCreate}>
              <Plus size={16} />
              New Basic Part
            </SecondaryButton>
          </HeaderActions>
        </Header>

        {loading ? (
          <EmptyState>Loading basic parts...</EmptyState>
        ) : sortedItems.length === 0 ? (
          <EmptyState>No basic parts are configured yet.</EmptyState>
        ) : (
          <PassiveList>
            {sortedItems.map((item) => (
              <PassiveCard
                key={item.id}
                type="button"
                $active={String(selectedId) === String(item.id) && isEditing}
                $thumbWidth={CATALOG_THUMB_WIDTH}
                onClick={() => startEdit(item)}
              >
                <PassiveThumb $width={CATALOG_THUMB_WIDTH} $height={CATALOG_THUMB_HEIGHT}>
                  <PassiveThumbSvg dangerouslySetInnerHTML={{ __html: getPassivePreviewData(item).markup || renderPreviewSvg(item.svgContent) }} />
                </PassiveThumb>

                <PassiveInfo>
                  <PassiveName>{item.name}</PassiveName>

                  <PassiveMeta>
                    <MetaBadge>{getPassiveTemplateLabel(item.templateType)}</MetaBadge>
                    <MetaBadge $tone="muted">Key: {item.componentKey}</MetaBadge>
                    {item.valueOptions.length > 0 && (
                      <MetaBadge $tone="muted">{item.valueOptions.length} values</MetaBadge>
                    )}
                    {item.showNoSimulationTag && (
                      <MetaBadge $tone="warning">No Simulation</MetaBadge>
                    )}
                    {!item.isActive && (
                      <MetaBadge $tone="warning">Inactive</MetaBadge>
                    )}
                    {item.isSystem && (
                      <MetaBadge $tone="muted">Built-in</MetaBadge>
                    )}
                  </PassiveMeta>

                  <PassiveDescription>
                    {item.description || 'No description provided.'}
                  </PassiveDescription>
                </PassiveInfo>
              </PassiveCard>
            ))}
          </PassiveList>
        )}
      </Panel>

      <Panel>
        <Header>
          <div>
            <Title>
              <Wrench size={18} />
              {isEditing ? 'Edit Basic Part' : 'Create Basic Part'}
            </Title>
            <Text>
              Choose a builder template, upload the SVG artwork, then save it into the Builder basic parts catalog.
            </Text>
          </div>
        </Header>

        <Form onSubmit={handleSubmit}>
          <Grid>
            <FormGroup>
              <Label htmlFor="passive-name">Name</Label>
              <Input
                id="passive-name"
                value={form.name}
                onChange={(event) => handleNameChange(event.target.value)}
                placeholder="Capacitor"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="passive-key">Component Key</Label>
              <Input
                id="passive-key"
                value={form.componentKey}
                onChange={(event) => handleFieldChange('componentKey', slugifyKey(event.target.value))}
                placeholder="capacitor"
                disabled={isEditing}
              />
              <HelperText>
                Stable internal key used by saved projects. It becomes read-only after creation.
              </HelperText>
            </FormGroup>
          </Grid>

          <Grid>
            <FormGroup>
              <Label htmlFor="passive-template">Template</Label>
              <Select
                id="passive-template"
                value={form.templateType}
                onChange={(event) => handleTemplateChange(event.target.value)}
                disabled={isEditing}
              >
                {PASSIVE_TEMPLATE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <HelperText>
                Template controls how the builder treats the part's pins and built-in behavior.
              </HelperText>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="passive-sort-order">Sort Order</Label>
              <Input
                id="passive-sort-order"
                type="number"
                value={form.sortOrder}
                onChange={(event) => handleFieldChange('sortOrder', event.target.value)}
              />
            </FormGroup>
          </Grid>

          <FormGroup>
            <Label htmlFor="passive-description">Description</Label>
            <TextArea
              id="passive-description"
              value={form.description}
              onChange={(event) => handleFieldChange('description', event.target.value)}
              placeholder="Describe how this passive is typically used."
            />
          </FormGroup>

          <Grid>
            <FormGroup>
              <Label htmlFor="passive-canvas-width">Canvas Width</Label>
              <Input
                id="passive-canvas-width"
                type="number"
                value={form.canvasWidth}
                onChange={(event) => handleFieldChange('canvasWidth', event.target.value)}
              />
              <HelperText>Rendered width inside Builder.</HelperText>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="passive-canvas-height">Canvas Height</Label>
              <Input
                id="passive-canvas-height"
                type="number"
                value={form.canvasHeight}
                onChange={(event) => handleFieldChange('canvasHeight', event.target.value)}
              />
              <HelperText>Rendered height inside Builder.</HelperText>
            </FormGroup>
          </Grid>

          <FormGroup>
            <ToggleLabel>
              <Toggle
                type="checkbox"
                checked={Boolean(form.isActive)}
                onChange={(event) => handleFieldChange('isActive', event.target.checked)}
              />
              Show this basic part in Builder
            </ToggleLabel>
          </FormGroup>

          <FormGroup>
            <ToggleLabel>
              <Toggle
                type="checkbox"
                checked={Boolean(form.showNoSimulationTag)}
                onChange={(event) => handleFieldChange('showNoSimulationTag', event.target.checked)}
              />
              Show a "No Simulation" tag in Builder
            </ToggleLabel>
            <HelperText>
              Turn this on for parts that are available visually but do not have a working browser simulator model yet.
            </HelperText>
          </FormGroup>

          <FormGroup>
            <Label>SVG Artwork</Label>
            <UploadBox>
              <Actions>
                <SecondaryButton type="button" onClick={handleBrowseSvg}>
                  <Upload size={16} />
                  Upload SVG
                </SecondaryButton>
              </Actions>

              <HiddenFileInput
                ref={fileInputRef}
                type="file"
                accept=".svg,image/svg+xml"
                onChange={handleSvgUpload}
              />

              <UploadMeta>
                {form.svgFilename || (form.svgContent ? 'Using inline SVG artwork.' : 'No SVG artwork selected yet.')}
              </UploadMeta>

              <HelperText>
                For custom generic basic parts, upload a Fritzing-style SVG that includes connector pin rectangles.
              </HelperText>
            </UploadBox>
          </FormGroup>

          <PreviewToolbar>
            <PreviewToolbarText>Basic Part Preview</PreviewToolbarText>

            <ZoomControls>
              <ZoomButton
                type="button"
                onClick={() => updateEditorPreviewZoom(editorPreviewZoom - PREVIEW_ZOOM_STEP)}
                disabled={editorPreviewZoom <= PREVIEW_ZOOM_MIN}
                aria-label="Zoom out basic part preview"
              >
                <Minus size={16} />
              </ZoomButton>
              <ZoomReadout>{Math.round(editorPreviewZoom * 100)}%</ZoomReadout>
              <ZoomButton
                type="button"
                onClick={() => updateEditorPreviewZoom(editorPreviewZoom + PREVIEW_ZOOM_STEP)}
                disabled={editorPreviewZoom >= PREVIEW_ZOOM_MAX}
                aria-label="Zoom in basic part preview"
              >
                <Plus size={16} />
              </ZoomButton>
              <ZoomResetButton
                type="button"
                onClick={() => updateEditorPreviewZoom(PREVIEW_ZOOM_DEFAULT)}
                disabled={editorPreviewZoom === PREVIEW_ZOOM_DEFAULT}
              >
                Reset
              </ZoomResetButton>
            </ZoomControls>
          </PreviewToolbar>

          <PreviewFrame>
            <PreviewSurface
              $width={previewCanvasDisplay.surfaceWidth}
              $height={previewCanvasDisplay.surfaceHeight}
            >
              <PreviewCanvasBoundary
                $width={previewCanvasDisplay.width}
                $height={previewCanvasDisplay.height}
              >
                <PreviewCanvasBadge>
                  Canvas {formatPreviewDimension(previewCanvasSize.width)} x {formatPreviewDimension(previewCanvasSize.height)}
                </PreviewCanvasBadge>
                {previewData.markup ? (
                  <PreviewSvg dangerouslySetInnerHTML={{ __html: previewData.markup }} />
                ) : (
                  <PreviewEmptyState>
                    SVG preview will appear here after you upload artwork.
                  </PreviewEmptyState>
                )}
              </PreviewCanvasBoundary>
            </PreviewSurface>
          </PreviewFrame>

          <PreviewMeta>
            <HelperText>
              The dashed outline marks the current Builder canvas footprint so you can line the SVG up with the saved width and height.
            </HelperText>
            <HelperText>
              Builder snaps wires to `.pin-hole` and `.pin-rect` markers. For generic SVGs, those usually come from Fritzing connector ids like `connector0pin` or `connector1terminal`.
            </HelperText>
            <HelperText>
              {detectedPinKeys.length > 0
                ? `Detected pin markers: ${detectedPinKeys.join(', ')}.`
                : 'No pin markers detected yet.'}
              {missingPinKeys.length > 0
                ? ` Missing template pins: ${missingPinKeys.join(', ')}.`
                : ''}
            </HelperText>
          </PreviewMeta>

          <FormGroup>
            <Label>Pin Offsets</Label>
            <PinOffsetPanel>
              <HelperText>
                Use X/Y nudges when the snap point lands too close to the body instead of the lead tip. The preview markers update immediately.
              </HelperText>

              <PinOffsetHeader>
                <div>Pin</div>
                <div>Offset X</div>
                <div>Offset Y</div>
              </PinOffsetHeader>

              {templatePinKeys.map((pinKey) => (
                <PinOffsetRow key={pinKey}>
                  <PinOffsetKey>{pinKey}</PinOffsetKey>
                  <Input
                    type="number"
                    step="0.1"
                    value={form.pinOffsets?.[pinKey]?.x ?? 0}
                    onChange={(event) => handlePinOffsetChange(pinKey, 'x', event.target.value)}
                    placeholder="0"
                  />
                  <Input
                    type="number"
                    step="0.1"
                    value={form.pinOffsets?.[pinKey]?.y ?? 0}
                    onChange={(event) => handlePinOffsetChange(pinKey, 'y', event.target.value)}
                    placeholder="0"
                  />
                </PinOffsetRow>
              ))}
            </PinOffsetPanel>
          </FormGroup>

          <FormGroup>
            <Label>Simulation Model</Label>
            <SimulationPanel>
              <SimulationHeader>
                <div>
                  <SimulationTitle>Runtime Behavior Setup</SimulationTitle>
                  <HelperText>
                    Define how this basic part should behave in the browser simulator. Template pins are: {templatePinKeys.join(', ')}.
                  </HelperText>
                </div>

                <SecondaryButton type="button" onClick={handleResetSimulationModel}>
                  Reset Model
                </SecondaryButton>
              </SimulationHeader>

              <Grid>
                <FormGroup>
                  <Label htmlFor="passive-simulation-model-key">Model Key</Label>
                  <Input
                    id="passive-simulation-model-key"
                    value={form.simulationModel?.modelKey ?? ''}
                    onChange={(event) => handleSimulationFieldChange('modelKey', event.target.value)}
                    placeholder="push_button"
                  />
                  <HelperText>
                    Stable internal identifier for this behavior profile.
                  </HelperText>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="passive-simulation-behavior">Behavior</Label>
                  <Select
                    id="passive-simulation-behavior"
                    value={form.simulationModel?.behavior ?? 'none'}
                    onChange={(event) => handleSimulationFieldChange('behavior', event.target.value)}
                  >
                    {PASSIVE_SIMULATION_BEHAVIOR_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>
              </Grid>

              <Grid>
                <FormGroup>
                  <Label htmlFor="passive-simulation-interaction">Interaction</Label>
                  <Select
                    id="passive-simulation-interaction"
                    value={form.simulationModel?.interaction ?? 'none'}
                    onChange={(event) => handleSimulationFieldChange('interaction', event.target.value)}
                  >
                    {PASSIVE_SIMULATION_INTERACTION_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="passive-simulation-notes">Model Notes</Label>
                  <Input
                    id="passive-simulation-notes"
                    value={form.simulationModel?.notes ?? ''}
                    onChange={(event) => handleSimulationFieldChange('notes', event.target.value)}
                    placeholder="Used as a pull-up input button."
                  />
                </FormGroup>
              </Grid>

              <SimulationSection>
                <SimulationSubTitle>Terminal Roles</SimulationSubTitle>
                <RoleGridHeader>
                  <div>Pin</div>
                  <div>Role</div>
                </RoleGridHeader>

                {templatePinKeys.map((pinKey) => (
                  <RoleGridRow key={pinKey}>
                    <PinOffsetKey>{pinKey}</PinOffsetKey>
                    <Input
                      value={form.simulationModel?.terminalRoles?.[pinKey] ?? ''}
                      onChange={(event) => handleSimulationTerminalRoleChange(pinKey, event.target.value)}
                      placeholder="terminal"
                    />
                  </RoleGridRow>
                ))}
              </SimulationSection>

              <SimulationSection>
                <SimulationSubTitle>Conduction Rules</SimulationSubTitle>
                <HelperText>
                  Use rules for direct continuity paths like resistors, switches, and future relay-style parts.
                </HelperText>

                <RuleHeader>
                  <div>From Pin</div>
                  <div>To Pin</div>
                  <div>Condition</div>
                  <div />
                </RuleHeader>

                {(form.simulationModel?.conductionRules || []).length === 0 ? (
                  <HelperText>No conduction rules defined yet.</HelperText>
                ) : (
                  (form.simulationModel?.conductionRules || []).map((rule, index) => (
                    <RuleRow key={`simulation-rule-${index}`}>
                      <Select
                        value={rule?.fromPinKey ?? templatePinKeys[0] ?? ''}
                        onChange={(event) => handleConductionRuleChange(index, 'fromPinKey', event.target.value)}
                      >
                        {templatePinKeys.map((pinKey) => (
                          <option key={`${index}-from-${pinKey}`} value={pinKey}>
                            {pinKey}
                          </option>
                        ))}
                      </Select>

                      <Select
                        value={rule?.toPinKey ?? templatePinKeys[1] ?? templatePinKeys[0] ?? ''}
                        onChange={(event) => handleConductionRuleChange(index, 'toPinKey', event.target.value)}
                      >
                        {templatePinKeys.map((pinKey) => (
                          <option key={`${index}-to-${pinKey}`} value={pinKey}>
                            {pinKey}
                          </option>
                        ))}
                      </Select>

                      <Select
                        value={rule?.when ?? 'always'}
                        onChange={(event) => handleConductionRuleChange(index, 'when', event.target.value)}
                      >
                        {PASSIVE_SIMULATION_RULE_CONDITION_OPTIONS.map((option) => (
                          <option key={`${index}-when-${option.value}`} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Select>

                      <InlineDangerButton
                        type="button"
                        onClick={() => handleRemoveConductionRule(index)}
                      >
                        Remove
                      </InlineDangerButton>
                    </RuleRow>
                  ))
                )}

                <Actions>
                  <SecondaryButton type="button" onClick={handleAddConductionRule}>
                    <Plus size={16} />
                    Add Rule
                  </SecondaryButton>
                </Actions>
              </SimulationSection>

              {supportsVariableResistorSensorControls && (
                <SimulationSection>
                  <SimulationSubTitle>Sensor Stimulus</SimulationSubTitle>
                  <HelperText>
                    Enable this when the part is still a variable resistor electrically, but the simulator should expose a real-world stimulus like light or temperature to the user.
                  </HelperText>

                  <ToggleLabel>
                    <Toggle
                      type="checkbox"
                      checked={Boolean(
                        simulationParametersDraft?.stimulusEnabled ??
                        variableResistorSensorState.hasStimulusControl
                      )}
                      onChange={(event) => handleSimulationParameterFieldChange(
                        'stimulusEnabled',
                        event.target.checked
                      )}
                    />
                    Expose a stimulus control in the simulator
                  </ToggleLabel>

                  {variableResistorSensorState.hasStimulusControl && (
                    <>
                      <Grid>
                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-label">Stimulus Label</Label>
                          <Input
                            id="passive-sensor-stimulus-label"
                            value={simulationParametersDraft?.stimulusLabel ?? ''}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusLabel',
                              event.target.value
                            )}
                            placeholder="Light Intensity"
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-unit">Stimulus Unit</Label>
                          <Input
                            id="passive-sensor-stimulus-unit"
                            value={simulationParametersDraft?.stimulusUnit ?? ''}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusUnit',
                              event.target.value
                            )}
                            placeholder="lux"
                          />
                        </FormGroup>
                      </Grid>

                      <Grid>
                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-min">Stimulus Min</Label>
                          <Input
                            id="passive-sensor-stimulus-min"
                            type="number"
                            value={simulationParametersDraft?.stimulusMin ?? variableResistorSensorState.stimulusMin}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusMin',
                              event.target.value === '' ? '' : Number(event.target.value),
                              { removeIfBlank: true }
                            )}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-max">Stimulus Max</Label>
                          <Input
                            id="passive-sensor-stimulus-max"
                            type="number"
                            value={simulationParametersDraft?.stimulusMax ?? variableResistorSensorState.stimulusMax}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusMax',
                              event.target.value === '' ? '' : Number(event.target.value),
                              { removeIfBlank: true }
                            )}
                          />
                        </FormGroup>
                      </Grid>

                      <Grid>
                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-step">Stimulus Step</Label>
                          <Input
                            id="passive-sensor-stimulus-step"
                            type="number"
                            min="0.001"
                            step="0.001"
                            value={simulationParametersDraft?.stimulusStep ?? variableResistorSensorState.stimulusStep}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusStep',
                              event.target.value === '' ? '' : Number(event.target.value),
                              { removeIfBlank: true }
                            )}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Label htmlFor="passive-sensor-stimulus-value">Initial Stimulus Value</Label>
                          <Input
                            id="passive-sensor-stimulus-value"
                            type="number"
                            value={simulationParametersDraft?.stimulusValue ?? variableResistorSensorState.stimulusValue}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusValue',
                              event.target.value === '' ? '' : Number(event.target.value),
                              { removeIfBlank: true }
                            )}
                          />
                        </FormGroup>
                      </Grid>

                      <Grid>
                        <FormGroup>
                          <Label htmlFor="passive-sensor-output-mode">Formula Output</Label>
                          <Select
                            id="passive-sensor-output-mode"
                            value={simulationParametersDraft?.stimulusOutputMode ?? variableResistorSensorState.stimulusOutputMode}
                            onChange={(event) => handleSimulationParameterFieldChange(
                              'stimulusOutputMode',
                              event.target.value
                            )}
                          >
                            {VARIABLE_RESISTOR_STIMULUS_OUTPUT_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </Select>
                        </FormGroup>

                        <FormGroup>
                          <ToggleLabel style={{ marginTop: 28 }}>
                            <Toggle
                              type="checkbox"
                              checked={Boolean(
                                simulationParametersDraft?.stimulusInvert ??
                                variableResistorSensorState.stimulusInvert
                              )}
                              onChange={(event) => handleSimulationParameterFieldChange(
                                'stimulusInvert',
                                event.target.checked
                              )}
                            />
                            Invert the default linear response
                          </ToggleLabel>
                        </FormGroup>
                      </Grid>

                      <FormGroup>
                        <Label htmlFor="passive-sensor-formula">Stimulus Formula</Label>
                        <TextArea
                          id="passive-sensor-formula"
                          value={simulationParametersDraft?.stimulusFormula ?? ''}
                          onChange={(event) => handleSimulationParameterFieldChange(
                            'stimulusFormula',
                            event.target.value,
                            { removeIfBlank: true }
                          )}
                          placeholder={'maxResistanceOhms - (normalizedStimulus * resistanceRangeOhms)'}
                          spellCheck={false}
                        />
                        {sensorFormulaError ? (
                          <StatusText $tone="error">{sensorFormulaError}</StatusText>
                        ) : (
                          <HelperText>
                            Optional. Available variables: `stimulus`, `stimulusMin`, `stimulusMax`, `stimulusRange`, `normalizedStimulus`, `minResistanceOhms`, `maxResistanceOhms`, `resistanceRangeOhms`, `minPosition`, `maxPosition`, and `positionRange`. Supported functions: `min()`, `max()`, `clamp()`, `abs()`, `pow()`, `sqrt()`, `log()`, `exp()`, `round()`, `floor()`, and `ceil()`.
                          </HelperText>
                        )}
                      </FormGroup>
                    </>
                  )}
                </SimulationSection>
              )}

              <SimulationSection>
                <SimulationSubTitle>Parameters JSON</SimulationSubTitle>
                <TextArea
                  value={form.simulationParametersText}
                  onChange={(event) => handleFieldChange('simulationParametersText', event.target.value)}
                  placeholder={'{\n  "debounceMs": 25\n}'}
                  spellCheck={false}
                />
                {simulationParametersError ? (
                  <StatusText $tone="error">{simulationParametersError}</StatusText>
                ) : (
                  <HelperText>
                    Store extra behavior data here, like thresholds, debounce timing, or actuator metadata. Leave this blank when the part does not need simulator-specific parameters.
                  </HelperText>
                )}
              </SimulationSection>

              <SimulationSection>
                <SimulationSubTitle>Normalized Preview</SimulationSubTitle>
                <HelperText>
                  This is the exact simulation model shape Builder will receive after normalization.
                </HelperText>
                <JsonPreview>{JSON.stringify(normalizedSimulationModel, null, 2)}</JsonPreview>
              </SimulationSection>
            </SimulationPanel>
          </FormGroup>

          <FormGroup>
            <Label>Selectable Values</Label>
            <ValueRowList>
              {(form.valueOptions || []).length === 0 ? (
                <HelperText>This basic part currently has no selectable values.</HelperText>
              ) : (
                form.valueOptions.map((option, index) => (
                  <ValueRow key={`${form.componentKey || 'new'}-${index}`}>
                    <Input
                      value={option.label ?? ''}
                      onChange={(event) => handleValueOptionChange(index, 'label', event.target.value)}
                      placeholder="Label"
                    />
                    <Input
                      value={option.value ?? ''}
                      onChange={(event) => handleValueOptionChange(index, 'value', event.target.value)}
                      placeholder="Value"
                    />
                    <InlineDangerButton
                      type="button"
                      onClick={() => handleRemoveValueOption(index)}
                    >
                      Remove
                    </InlineDangerButton>
                  </ValueRow>
                ))
              )}
            </ValueRowList>

            <Actions>
              <SecondaryButton type="button" onClick={handleAddValueOption}>
                <Plus size={16} />
                Add Value
              </SecondaryButton>
            </Actions>
          </FormGroup>

          {(form.valueOptions || []).length > 0 && (
            <FormGroup>
              <Label htmlFor="passive-default-value">Default Value</Label>
              <Select
                id="passive-default-value"
                value={form.defaultValue ?? ''}
                onChange={(event) => handleFieldChange('defaultValue', event.target.value)}
              >
                {(form.valueOptions || []).map((option, index) => (
                  <option key={`${index}-${option.label}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}

          <FormFooter>
            <div>
              {error && <StatusText $tone="error">{error}</StatusText>}
              {!error && success && <StatusText $tone="success">{success}</StatusText>}
              {!error && !success && (
                <StatusText>
                  Editing template: {getPassiveTemplateLabel(form.templateType)}
                </StatusText>
              )}
            </div>

            <Actions>
              {isEditing && selectedItem && !selectedItem.isSystem && (
                <InlineDangerButton
                  type="button"
                  onClick={() => handleDelete(selectedItem)}
                  disabled={saving}
                >
                  <Trash2 size={16} />
                  Delete
                </InlineDangerButton>
              )}

              <PrimaryButton type="submit" disabled={saving}>
                <Save size={16} />
                {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Basic Part'}
              </PrimaryButton>
            </Actions>
          </FormFooter>
        </Form>
      </Panel>
    </Wrapper>
  );
};

export default ProjectBuilderPassiveManager;
